import * as React from 'react';
import { SubWindowContext, SubWindowState } from './sub-window.viewmodel';

interface ISubWindowButtonProps {
    onClick(): void;
}

/**
 * HOC creator function,
 * takes then button presentation and ensure that by clicking on this button, sub-window, hosting this button, is being closed.
 *
 * Built button component might receive optional 'onClick' prop of type `() => Promise<boolean> | boolean`.
 * This let's control whether the sub-window should be closed from within the particular business feature
 * and can be used, for example, when we hosting the form inside the sub-window and 'Submit' button
 * shall not close the sub-window unless form it successfully saved
 *
 * @param CloseButtonPresentation
 */
function createCloseSubWindowButton<T>(
    CloseButtonPresentation: React.ComponentClass<T & ISubWindowButtonProps> | React.FunctionComponent<T & ISubWindowButtonProps>,
) {
    return (props: T & { onClick?: () => Promise<boolean | void> | boolean | void } ) => (
        <SubWindowContext.Consumer>
            {context => {
                // if click handler passed to HOC - execute it before closing sub-window
                const clickHandlerHOCClosingSubWindow = props.onClick ? () => {
                    const actionResult = props.onClick();
                    // if click handler return promise - wait until it is ended and close sub-window only if `true` is returned from promise.
                    if (actionResult instanceof Promise) {
                        actionResult.then((result: boolean) => result && context.closeSubWindow());
                    } else {
                        // if click handler return promise - close window if this boolean is true
                        if (actionResult) {
                            context.closeSubWindow();
                        }
                    }
                } : context.getParameters().onCloseIconClickHandler ?
                    context.getParameters().onCloseIconClickHandler :
                    context.closeSubWindow;

                return (<CloseButtonPresentation { ...props} onClick={clickHandlerHOCClosingSubWindow} />);
            }}
        </SubWindowContext.Consumer>
    );
}

/**
 * HOC creator function,
 * takes then button presentation and ensure that by clicking on this button, sub-window, hosting this button, is being maximized or minimized
 *
 * @param ChangeMinimizedStateButtonPresentation
 * @param requestedMinimizedState - if true - sub-window will be minimized by clicking on returned HOC, if false - maximized
 */
function createChangeMinimizeSubWindowButton<T>(
    ChangeMinimizedStateButtonPresentation: React.ComponentClass<T & ISubWindowButtonProps> | React.FunctionComponent<T & ISubWindowButtonProps>,
    requestedMinimizedState: boolean
) {
    return (props: T) => (
        <SubWindowContext.Consumer>
            {context => {
                const clickHandler = () => {
                    context.changeState(requestedMinimizedState ? SubWindowState.MINIMIZED : SubWindowState.MAXIMIZED);
                };

                return (<ChangeMinimizedStateButtonPresentation {...props} onClick={clickHandler}/>);
            }}
        </SubWindowContext.Consumer>
    );
}

export { createCloseSubWindowButton, ISubWindowButtonProps, createChangeMinimizeSubWindowButton };